import styled from "styled-components";
import { useEffect, useState } from "react";
import { RiCheckboxCircleFill, RiCloseCircleFill } from "react-icons/ri";
import Overlay from "../../Componentes/Overlay";
import Modal from "../../Componentes/Utils/Modal";
import DataTable from "../../Componentes/Utils/DataTable";
import SmallLoading from "../../Componentes/Utils/SmallLoading";
import useApi from "../../hooks/useApi";
import { formatCurrency } from "../../Funcoes/formatCurrency";
import { useToast } from "../../contexts/ToastContext";

const DETALHES_PC_URL = "Painel/API/Controladoria/PedidosCompra/VinculoNf/Detalhes/index.php";
const VERIFICAR_URL = "Painel/API/Controladoria/PedidosCompra/VerificarItens/index.php";

const Title = styled.h2`
    margin: 0 0 12px 0;
    font-size: 18px;
    color: var(--ctrl-text, #333);
`;

const InfoGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 10px 16px;
    margin-bottom: 18px;

    @media (max-width: 700px) {
        grid-template-columns: repeat(2, 1fr);
    }
`;

const InfoItem = styled.div`
    display: flex;
    flex-direction: column;
    gap: 2px;
`;

const InfoLabel = styled.span`
    font-size: 11px;
    font-weight: 600;
    text-transform: uppercase;
    color: var(--ctrl-label, #94a3b8);
`;

const InfoValue = styled.span`
    font-size: 14px;
    color: var(--ctrl-text, #1f2937);
    font-weight: 500;
`;

const SectionTitle = styled.h3`
    margin: 18px 0 8px 0;
    font-size: 14px;
    color: var(--ctrl-text-muted, #334155);
    font-weight: 700;
`;

const TableWrapper = styled.div`
    width: 100%;
    max-height: 26dvh;
    overflow: auto;
    border: 1px solid var(--ctrl-border, #ddd);
    border-radius: 8px;
`;

const EmptyText = styled.p`
    margin: 0;
    padding: 12px;
    font-size: 14px;
    color: var(--ctrl-text-muted, #666);
`;

const LoadingWrapper = styled.div`
    width: 100%;
    min-height: 200px;
    display: flex;
    align-items: center;
    justify-content: center;
`;

const Coluna = styled.td`
    text-align: ${({ align }) => align || "left"};
`;

const TotalRow = styled.tr`
    background-color: var(--ctrl-surface-soft, #f8fafc);
    font-weight: 700;

    & td {
        border-top: 2px solid var(--ctrl-border-soft, #e2e8f0);
    }
`;

const Footer = styled.div`
    display: flex;
    justify-content: flex-end;
    align-items: center;
    gap: 12px;
    margin-top: 18px;
`;

const AnalisarBtn = styled.button`
    height: 36px;
    padding: 0 18px;
    border: none;
    border-radius: 4px;
    background-color: ${(props) => (props.disabled ? "#cbd5e1" : "#2563eb")};
    color: #fff;
    font-weight: 600;
    cursor: ${(props) => (props.disabled ? "not-allowed" : "pointer")};

    &:hover {
        background-color: ${(props) => (props.disabled ? "#cbd5e1" : "#1d4ed8")};
    }
`;

const ConfirmarBtn = styled(AnalisarBtn)`
    background-color: ${(props) => (props.disabled ? "#cbd5e1" : "#16a34a")};

    &:hover {
        background-color: ${(props) => (props.disabled ? "#cbd5e1" : "#15803d")};
    }
`;

const StatusBanner = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 10px 14px;
    border-radius: 6px;
    font-size: 14px;
    font-weight: 600;
    margin: 16px 0 6px 0;
    border: 1px solid;

    ${(props) =>
        props.$variant === "completo"
            ? "background:#dcfce7;color:#166534;border-color:#86efac;"
            : props.$variant === "parcial"
            ? "background:#fef9c3;color:#854d0e;border-color:#fde047;"
            : "background:#fee2e2;color:#991b1b;border-color:#fecaca;"}

    & svg {
        width: 18px;
        height: 18px;
        flex-shrink: 0;
    }
`;

const ItemCard = styled.div`
    border: 1px solid var(--ctrl-border-soft, #e2e8f0);
    border-left: 4px solid ${(props) => (props.$ok ? "#22c55e" : "#ef4444")};
    border-radius: 6px;
    padding: 10px 12px;
    margin-bottom: 8px;
    background: var(--ctrl-surface, #fff);
`;

const ItemHeader = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 13px;
    font-weight: 600;
    color: var(--ctrl-text, #1f2937);

    & svg {
        width: 16px;
        height: 16px;
        color: ${(props) => (props.$ok ? "#16a34a" : "#dc2626")};
    }
`;

const Motivo = styled.span`
    font-size: 12px;
    color: #b91c1c;
    font-weight: 500;
`;

const Chips = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    margin-top: 8px;
`;

const Chip = styled.span`
    display: inline-flex;
    padding: 3px 8px;
    border-radius: 12px;
    font-size: 11px;
    font-weight: 600;
    background: ${(props) => (props.$ok ? "#f0fdf4" : "#fef2f2")};
    color: ${(props) => (props.$ok ? "#15803d" : "#b91c1c")};
    border: 1px solid ${(props) => (props.$ok ? "#bbf7d0" : "#fecaca")};

    & small {
        font-weight: 400;
        opacity: 0.85;
    }
`;

const ErroMsg = styled.p`
    margin: 12px 0 0 0;
    padding: 10px 12px;
    border-radius: 6px;
    background: #fef2f2;
    color: #b91c1c;
    font-size: 13px;
    border: 1px solid #fecaca;
`;

const ConfList = styled.div`
    max-height: 28dvh;
    overflow: auto;
    padding-right: 4px;
`;

const CAMPO_LABELS = {
    ncm: "NCM",
    unidade: "Unid.",
    quantidade: "Qtd.",
    valor_unit: "V. Unit.",
    valor_total: "V. Total",
};

const formatQuantidade = (value) => {
    const numValue = typeof value === "string" ? parseFloat(value) : value;
    if (value == null || isNaN(numValue)) return " - ";
    return numValue.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const formatCampoValor = (campo, valor) => {
    if (valor == null || valor === "") return "-";
    if (campo === "ncm" || campo === "unidade") return String(valor);
    if (campo === "quantidade") return formatQuantidade(valor);
    return `R$ ${formatCurrency(valor)}`;
};

const ModalItensPc = ({ nf, pedido, onClose, onVinculado }) => {
    const api = useApi();
    const { notifyError } = useToast();

    const [carregando, setCarregando] = useState(true);
    const [itensPc, setItensPc] = useState([]);
    const [erroItens, setErroItens] = useState(null);

    const [analise, setAnalise] = useState(null);
    const [analisando, setAnalisando] = useState(false);
    const [salvando, setSalvando] = useState(false);
    const [erro, setErro] = useState(null);

    useEffect(() => {
        let ativo = true;

        const carregarItens = async () => {
            setCarregando(true);
            setErroItens(null);

            try {
                const params = new URLSearchParams();
                params.append("filial", pedido.FILIAL);
                params.append("num_pedido", pedido.NUM_PEDIDO);

                const { data } = await api.get(`${DETALHES_PC_URL}?${params.toString()}`);

                if (!ativo) return;

                if (!data?.itens) {
                    setErroItens("Falha ao carregar os itens do pedido.");
                    return;
                }

                setItensPc(data.itens || []);
            } catch (e) {
                notifyError(e?.response?.data?.message || "Erro ao carregar os itens do pedido.");
                if (ativo) setErroItens("Erro ao carregar os itens do pedido.");
            } finally {
                if (ativo) setCarregando(false);
            }
        };

        carregarItens();

        return () => {
            ativo = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const conferir = async (salvar = false) => {
        if (!nf?.NUM_NF || !pedido) return;

        setErro(null);
        salvar ? setSalvando(true) : setAnalisando(true);

        try {
            const { data } = await api.post(VERIFICAR_URL, {
                filial: pedido.FILIAL,
                num_pedido: pedido.NUM_PEDIDO,
                num_nf: nf.NUM_NF,
                cod_fornecedor: nf.FORNECEDOR_COD || pedido.FORNECEDOR_COD || "",
                salvar,
            });

            if (!data?.success) {
                setErro(data?.message || "Falha ao conferir com o pedido.");
                return;
            }

            setAnalise(data);

            if (salvar && data.salvo) {
                onVinculado?.();
            }
        } catch (e) {
            notifyError(e?.response?.data?.message || "Erro ao conferir com o pedido. Tente novamente.");
            setErro("Erro ao conferir com o pedido. Tente novamente.");
        } finally {
            salvar ? setSalvando(false) : setAnalisando(false);
        }
    };

    const conferenciaOk = analise?.conferencia_ok;
    const variante = conferenciaOk ? (analise.completa ? "completo" : "parcial") : "erro";
    const totalPc = itensPc.reduce((acc, item) => acc + (Number(item.VALOR_TOTAL) || 0), 0);

    return (
        <Overlay state={true}>
            <Modal size="lg" header close={onClose}>
                <Title>Itens do PC {pedido.NUM_PEDIDO}</Title>

                {carregando ? (
                    <LoadingWrapper>
                        <SmallLoading text="Carregando itens do pedido..." />
                    </LoadingWrapper>
                ) : erroItens ? (
                    <EmptyText>{erroItens}</EmptyText>
                ) : (
                    <>
                        <InfoGrid>
                            <InfoItem>
                                <InfoLabel>Filial</InfoLabel>
                                <InfoValue>{pedido.FILIAL || " - "}</InfoValue>
                            </InfoItem>
                            <InfoItem>
                                <InfoLabel>Pedido</InfoLabel>
                                <InfoValue>{pedido.NUM_PEDIDO || " - "}</InfoValue>
                            </InfoItem>
                            <InfoItem>
                                <InfoLabel>Data Emissão</InfoLabel>
                                <InfoValue>{pedido.DT_EMISSAO || " - "}</InfoValue>
                            </InfoItem>
                            <InfoItem>
                                <InfoLabel>NF selecionada</InfoLabel>
                                <InfoValue>{nf.NUM_NF || " - "}</InfoValue>
                            </InfoItem>
                        </InfoGrid>

                        <SectionTitle>Itens do Pedido de Compra</SectionTitle>
                        {itensPc.length === 0 ? (
                            <EmptyText>Nenhum item encontrado neste pedido.</EmptyText>
                        ) : (
                            <TableWrapper>
                                <DataTable>
                                    <thead>
                                        <tr>
                                            <th>COD. PRODUTO</th>
                                            <th>DESCRIÇÃO</th>
                                            <th>UNIDADE</th>
                                            <th>QUANTIDADE</th>
                                            <th>VALOR TOTAL</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {itensPc.map((item, index) => (
                                            <tr key={`${item.COD_PRODUTO}-${index}`}>
                                                <td>{item.COD_PRODUTO || " - "}</td>
                                                <td>{item.DESC_PRODUTO || " - "}</td>
                                                <Coluna align="center">{item.UNIDADE || " - "}</Coluna>
                                                <Coluna align="right">{formatQuantidade(item.QUANTIDADE)}</Coluna>
                                                <Coluna align="right">
                                                    {item.VALOR_TOTAL != null
                                                        ? `R$ ${formatCurrency(item.VALOR_TOTAL)}`
                                                        : " - "}
                                                </Coluna>
                                            </tr>
                                        ))}
                                    </tbody>
                                    <tfoot>
                                        <TotalRow>
                                            <td colSpan={4}>Valor total do pedido</td>
                                            <Coluna align="right">{`R$ ${formatCurrency(totalPc)}`}</Coluna>
                                        </TotalRow>
                                    </tfoot>
                                </DataTable>
                            </TableWrapper>
                        )}

                        {erro && <ErroMsg>{erro}</ErroMsg>}

                        {analise && (
                            <>
                                <StatusBanner $variant={variante}>
                                    {conferenciaOk ? <RiCheckboxCircleFill /> : <RiCloseCircleFill />}
                                    {conferenciaOk
                                        ? analise.completa
                                            ? analise.salvo
                                                ? "Vínculo salvo — pedido FINALIZADO."
                                                : "Conferência OK. Vai salvar como COMPLETO (finaliza o pedido)."
                                            : analise.salvo
                                            ? "Vínculo parcial salvo."
                                            : "Conferência OK. Vai salvar como PARCIAL (ainda há saldo a faturar)."
                                        : "Há divergências entre a NF e o pedido. Ajuste antes de vincular."}
                                </StatusBanner>

                                <ConfList>
                                    {analise.conferencia.map((it, idx) => (
                                        <ItemCard key={idx} $ok={it.ok}>
                                            <ItemHeader $ok={it.ok}>
                                                {it.ok ? <RiCheckboxCircleFill /> : <RiCloseCircleFill />}
                                                NF item {it.nitem}
                                                {it.c7_item ? ` → PC item ${it.c7_item}` : ""} · {it.descricao_nf}
                                            </ItemHeader>
                                            {it.campos ? (
                                                <Chips>
                                                    {Object.entries(it.campos).map(([campo, dados]) => (
                                                        <Chip key={campo} $ok={dados.ok}>
                                                            {CAMPO_LABELS[campo] || campo}:{" "}
                                                            <small>
                                                                {formatCampoValor(campo, dados.pc)} /{" "}
                                                                {formatCampoValor(campo, dados.nf)}
                                                            </small>
                                                        </Chip>
                                                    ))}
                                                </Chips>
                                            ) : (
                                                <Motivo>{it.motivo}</Motivo>
                                            )}
                                        </ItemCard>
                                    ))}
                                </ConfList>

                                {analise.saldo_itens?.length > 0 && (
                                    <>
                                        <SectionTitle>Saldo por item (pedido x faturado)</SectionTitle>
                                        <TableWrapper>
                                            <DataTable>
                                                <thead>
                                                    <tr>
                                                        <th>ITEM</th>
                                                        <th>PRODUTO</th>
                                                        <th>QTD PEDIDO</th>
                                                        <th>JÁ FATURADO</th>
                                                        <th>SALDO</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {analise.saldo_itens.map((s) => (
                                                        <tr key={s.c7_item}>
                                                            <Coluna align="center">{s.c7_item}</Coluna>
                                                            <td>{s.produto}</td>
                                                            <Coluna align="right">{formatQuantidade(s.qtd_pedido)}</Coluna>
                                                            <Coluna align="right">{formatQuantidade(s.qtd_faturada)}</Coluna>
                                                            <Coluna align="right">{formatQuantidade(s.qtd_saldo)}</Coluna>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </DataTable>
                                        </TableWrapper>
                                    </>
                                )}
                            </>
                        )}

                        <Footer>
                            <AnalisarBtn
                                type="button"
                                disabled={analisando || salvando || itensPc.length === 0}
                                onClick={() => conferir(false)}
                            >
                                {analisando ? "Analisando..." : "Analisar itens"}
                            </AnalisarBtn>
                            {conferenciaOk && !analise.salvo && (
                                <ConfirmarBtn type="button" disabled={salvando} onClick={() => conferir(true)}>
                                    {salvando
                                        ? "Salvando..."
                                        : `Confirmar vínculo (${analise.completa ? "COMPLETO" : "PARCIAL"})`}
                                </ConfirmarBtn>
                            )}
                        </Footer>
                    </>
                )}
            </Modal>
        </Overlay>
    );
};

export default ModalItensPc;
